/**
 * AddPublicationPopover — simple dialog for creating a new publication.
 * Opened from toolbar button OR from clicking an empty day cell.
 *
 * MVP feature set: strategy, platform, topic (optional), HH:mm time.
 * Week/weekday derived from the clicked date (or "today" when toolbar-triggered).
 */

import React, { useEffect, useState } from 'react';
import {
  Box, Button, Dialog, DialogActions, DialogContent, DialogTitle,
  FormControl, InputLabel, MenuItem, Select, Stack, TextField,
} from '@mui/material';
import { CONTENT_PLAN } from '@test-kit/selectors/content-plan';
import type { PlatformId, CalendarAwareContentStrategy, ContentPublication } from '../../types';
import { moveScheduledAtToDay } from '../../utils/calendarDates';

export interface AddPublicationPopoverProps {
  open: boolean;
  onClose: () => void;
  /** Pre-selected target date (from DayCell click). Defaults to today. */
  targetDate: Date | null;
  targetWeekIndex?: number;
  targetWeekdayIndex?: number;
  strategies: CalendarAwareContentStrategy[];
  platformOptions: PlatformId[];
  /** Default strategy id (locked in Mode A). */
  defaultStrategyId?: string;
  lockStrategy?: boolean;
  onCreate: (pub: Omit<ContentPublication, 'id' | 'projectId' | 'strategyId' | 'createdAt' | 'updatedAt'> & { strategyId: string }) => Promise<void>;
}

const DEFAULT_TIME = '09:00';

export const AddPublicationPopover: React.FC<AddPublicationPopoverProps> = ({
  open, onClose, targetDate, targetWeekIndex, targetWeekdayIndex,
  strategies, platformOptions, defaultStrategyId, lockStrategy, onCreate,
}) => {
  const [strategyId, setStrategyId] = useState<string>(defaultStrategyId ?? '');
  const [platform, setPlatform] = useState<PlatformId>(platformOptions[0] ?? ('' as PlatformId));
  const [topicId, setTopicId] = useState<string>('');
  const [timeLocal, setTimeLocal] = useState<string>(DEFAULT_TIME);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setStrategyId(defaultStrategyId ?? strategies[0]?.id ?? '');
      setPlatform(platformOptions[0] ?? ('' as PlatformId));
      setTopicId('');
      setTimeLocal(DEFAULT_TIME);
    }
  }, [open, defaultStrategyId, strategies, platformOptions]);

  const handleSave = async () => {
    if (!strategyId || !platform) return;
    setSaving(true);
    try {
      const day = targetDate ?? new Date();
      const [hh, mm] = timeLocal.split(':').map(Number);
      const scheduledBase = new Date(day);
      scheduledBase.setUTCHours(hh || 0, mm || 0, 0, 0);
      const scheduledAt = moveScheduledAtToDay(scheduledBase.toISOString(), day);
      await onCreate({
        strategyId,
        platform,
        topicId: topicId || undefined,
        scheduledAt,
        weekIndex: targetWeekIndex ?? 0,
        weekdayIndex: targetWeekdayIndex ?? 0,
        source: 'manual',
      });
      onClose();
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="xs"
      data-testid={CONTENT_PLAN.ADD_PUB_DIALOG}
    >
      <DialogTitle>Add publication</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 0.5 }}>
          <FormControl fullWidth size="small">
            <InputLabel>Strategy</InputLabel>
            <Select
              data-testid={CONTENT_PLAN.ADD_PUB_STRATEGY}
              label="Strategy"
              value={strategyId}
              onChange={(e) => setStrategyId(e.target.value)}
              disabled={lockStrategy}
            >
              {strategies.map((s) => (
                <MenuItem key={s.id} value={s.id}>
                  {(s as { name?: string; title?: string }).name ?? (s as { title?: string }).title ?? s.id}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth size="small">
            <InputLabel>Platform</InputLabel>
            <Select
              data-testid={CONTENT_PLAN.ADD_PUB_PLATFORM}
              label="Platform"
              value={platform}
              onChange={(e) => setPlatform(e.target.value as PlatformId)}
            >
              {platformOptions.map((option) => (
                <MenuItem key={option} value={option}>
                  {String(option)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            data-testid={CONTENT_PLAN.ADD_PUB_TOPIC}
            label="Topic id (optional)"
            size="small"
            value={topicId}
            onChange={(e) => setTopicId(e.target.value)}
            helperText="Leave empty for an empty slot"
          />

          <TextField
            data-testid={CONTENT_PLAN.ADD_PUB_TIME}
            label="Time (UTC)"
            type="time"
            size="small"
            value={timeLocal}
            onChange={(e) => setTimeLocal(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />

          {targetDate && (
            <Box sx={{ fontSize: 12, color: 'text.secondary' }}>
              Target day: {targetDate.toUTCString().slice(0, 16)}
            </Box>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button data-testid={CONTENT_PLAN.ADD_PUB_CANCEL} onClick={onClose}>Cancel</Button>
        <Button
          data-testid={CONTENT_PLAN.ADD_PUB_SAVE}
          onClick={handleSave}
          variant="contained"
          disabled={!strategyId || !platform || saving}
        >
          {saving ? 'Saving…' : 'Add'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
